// Signature capture — draw or type. Emits PNG (base64) via onChange.
import React, { useEffect, useRef, useState } from "react";
import { Pen, Type, Eraser } from "lucide-react";

export default function SignatureCanvas({ onChange, defaultName = "" }) {
  const [mode, setMode] = useState("draw"); // draw | type
  const [typed, setTyped] = useState(defaultName);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  // Set up canvas
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = "#0a1230";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, [mode]);

  useEffect(() => {
    if (mode === "type") {
      const png = renderTypedToPng(typed || defaultName);
      onChange && onChange(png);
    }
  }, [mode, typed, defaultName, onChange]);

  function pos(e) {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  }

  function start(e) {
    e.preventDefault();
    drawingRef.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  }

  function move(e) {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setHasInk(true);
  }

  function end(e) {
    if (!drawingRef.current) return;
    e.preventDefault();
    drawingRef.current = false;
    // Emit
    const c = canvasRef.current;
    const png = trimCanvas(c);
    onChange && onChange(png);
  }

  function clear() {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setHasInk(false);
    onChange && onChange("");
  }

  return (
    <div data-testid="signature-canvas" className="w-full">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => setMode("draw")}
          data-testid="mode-draw"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] transition"
          style={{
            background: mode === "draw" ? "var(--crm-accent-soft)" : "transparent",
            color: mode === "draw" ? "var(--crm-accent)" : "var(--crm-text-muted)",
            border: "1px solid var(--crm-line)",
          }}
        >
          <Pen size={12} /> Draw
        </button>
        <button
          type="button"
          onClick={() => setMode("type")}
          data-testid="mode-type"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] transition"
          style={{
            background: mode === "type" ? "var(--crm-accent-soft)" : "transparent",
            color: mode === "type" ? "var(--crm-accent)" : "var(--crm-text-muted)",
            border: "1px solid var(--crm-line)",
          }}
        >
          <Type size={12} /> Type
        </button>
        <div className="flex-1" />
        {mode === "draw" && hasInk && (
          <button
            type="button" onClick={clear} data-testid="clear-signature"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px]"
            style={{ color: "var(--crm-text-muted)" }}
          >
            <Eraser size={12} /> Clear
          </button>
        )}
      </div>

      {mode === "draw" ? (
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas-el"
          className="w-full h-40 rounded-xl touch-none"
          style={{ background: "#fbfbfd", border: "1px solid var(--crm-line)" }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
      ) : (
        <div className="h-40 rounded-xl flex items-center justify-center"
          style={{ background: "#fbfbfd", border: "1px solid var(--crm-line)" }}>
          <input
            data-testid="typed-signature-input"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Type your full legal name"
            className="text-[38px] focus:outline-none text-center bg-transparent"
            style={{
              fontFamily: "'Great Vibes', 'Snell Roundhand', 'Brush Script MT', cursive",
              color: "#0a1230",
              minWidth: 300,
            }}
          />
        </div>
      )}
      <div className="mt-2 text-[11px]" style={{ color: "var(--crm-text-subtle)" }}>
        By signing, you agree that your electronic signature is the legal equivalent of a manual signature.
      </div>
    </div>
  );
}

// -- helpers -----------------------------------------------------------------
function trimCanvas(src) {
  const ctx = src.getContext("2d");
  const w = src.width, h = src.height;
  const img = ctx.getImageData(0, 0, w, h).data;
  let minX = w, minY = h, maxX = 0, maxY = 0, found = false;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (img[(y * w + x) * 4 + 3] > 0) {
        found = true;
        if (x < minX) minX = x; if (y < minY) minY = y;
        if (x > maxX) maxX = x; if (y > maxY) maxY = y;
      }
    }
  }
  if (!found) return "";
  const pad = 8;
  minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
  maxX = Math.min(w - 1, maxX + pad); maxY = Math.min(h - 1, maxY + pad);
  const cw = maxX - minX + 1, ch = maxY - minY + 1;
  const out = document.createElement("canvas");
  out.width = cw; out.height = ch;
  out.getContext("2d").drawImage(src, minX, minY, cw, ch, 0, 0, cw, ch);
  return out.toDataURL("image/png");
}

function renderTypedToPng(name) {
  if (!name) return "";
  const c = document.createElement("canvas");
  c.width = 900; c.height = 220;
  const ctx = c.getContext("2d");
  ctx.font = "italic 110px 'Great Vibes', 'Snell Roundhand', 'Brush Script MT', cursive";
  ctx.fillStyle = "#0a1230";
  ctx.textBaseline = "middle";
  const w = ctx.measureText(name).width;
  ctx.fillText(name, (c.width - w) / 2, c.height / 2);
  return c.toDataURL("image/png");
}
